// Where the learner sees the tutor avatar on desktop (1024px and wider).
// Fullscreen is a temporary state on top of a view, never a stored view.
export type TutorView = "header" | "compact" | "large" | "none";
export type ShownTutorView = Exclude<TutorView, "none">;

/** The avatar starts compact in the conversation; learners can choose another view. */
export const DEFAULT_TUTOR_VIEW: TutorView = "compact";

export const TUTOR_VIEWS: Array<{ value: TutorView; label: string }> = [
  { value: "header", label: "Top bar" },
  { value: "compact", label: "Compact" },
  { value: "large", label: "Large" },
  { value: "none", label: "Off" },
];

/** Beside her, the full conversation by default; Latest message is a choice. */
export const DEFAULT_LATEST_ONLY = false;

const VIEW_KEY = "chickytutor:tutor-view";
// The camera button brings the avatar back in the last view she was shown in.
const SHOWN_VIEW_KEY = "chickytutor:tutor-view-shown";
const CONVERSATION_KEY = "chickytutor:tutor-conversation";

export function isTutorView(value: unknown): value is TutorView {
  return value === "header" || value === "compact" || value === "large" || value === "none";
}

function read(key: string): TutorView | null {
  try {
    const value = window.localStorage.getItem(key);
    return isTutorView(value) ? value : null;
  } catch {
    return null;
  }
}

function write(key: string, view: TutorView) {
  try {
    window.localStorage.setItem(key, view);
  } catch {
    // Storage can be unavailable (private mode); the view still applies this session.
  }
}

export function readStoredTutorView(): TutorView | null {
  return read(VIEW_KEY);
}

export function readStoredShownView(): ShownTutorView | null {
  const view = read(SHOWN_VIEW_KEY);
  return view && view !== "none" ? view : null;
}

export function storeTutorView(view: TutorView) {
  write(VIEW_KEY, view);
  if (view !== "none") write(SHOWN_VIEW_KEY, view);
}

export function readStoredLatestOnly(): boolean | null {
  try {
    const value = window.localStorage.getItem(CONVERSATION_KEY);
    return value === "latest" ? true : value === "full" ? false : null;
  } catch {
    return null;
  }
}

export function storeLatestOnly(latestOnly: boolean) {
  try {
    window.localStorage.setItem(CONVERSATION_KEY, latestOnly ? "latest" : "full");
  } catch {
    // Storage can be unavailable (private mode); the choice still applies this session.
  }
}
